/**
 * AdminAgents — Gestione Agenti, Sub-agenti e Piani Provvigionali (Fase 1)
 *
 * Rotte servite:
 *   /admin/agents                → tab Elenco Agenti
 *   /admin/agents/commission-plans → tab Piani Provvigionali
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import LoadingSpinner from '../LoadingSpinner';
import {
  Plus, Edit, Trash2, X, Save, Users, Percent, KeyRound, ChevronDown, ChevronRight,
  Building2, Copy, CheckCircle2, AlertTriangle, LayoutGrid,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminAgents = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { adminToken, adminLogout } = useAdminAuth();
  const { toast } = useToast();

  const activeTab = location.pathname.includes('commission-plans') ? 'plans' : 'agents';

  // Centralized handler per risposte 401 (token scaduto/invalido): logout + redirect a login.
  // Evita chiamate a catena e stati inconsistenti in UI.
  const handleUnauthorized = async () => {
    toast({
      title: 'Sessione scaduta',
      description: 'Effettua di nuovo il login per continuare.',
      variant: 'destructive',
    });
    try { await adminLogout(); } catch (e) { /* ignore */ }
    navigate('/admin/login');
  };

  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [editingAgent, setEditingAgent] = useState(null);
  const [creatingAgent, setCreatingAgent] = useState(null); // {parentAgentId?}
  const [editingPlan, setEditingPlan] = useState(null);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [expandedAgents, setExpandedAgents] = useState({});
  const [newlyCreated, setNewlyCreated] = useState(null); // {agent, initialPassword}
  const [approving, setApproving] = useState(null); // pending agent to approve

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${adminToken}`,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [aRes, pRes] = await Promise.all([
        fetch(`${API}/api/admin/agents`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/agents/commission-plans`, { headers: authHeaders() }),
      ]);
      if (aRes.status === 401 || pRes.status === 401) {
        await handleUnauthorized();
        return;
      }
      if (aRes.ok) setAgents((await aRes.json()).data || []);
      if (pRes.ok) setPlans((await pRes.json()).data || []);
    } catch (e) {
      toast({ title: 'Errore caricamento', description: String(e.message), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Se manca il token in memoria, redirect subito al login
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    load();
    /* eslint-disable-next-line */
  }, [adminToken]);

  // Costruisco l'albero: top-level agent → array di sub-agent
  const hierarchy = useMemo(() => {
    const topLevel = agents.filter((a) => a.userType === 'agent' && a.status !== 'pending_approval');
    return topLevel.map((a) => ({
      ...a,
      subAgents: agents.filter((s) => s.userType === 'sub-agent' && s.parentAgentId === a.id),
    }));
  }, [agents]);

  const pendingApplicants = useMemo(
    () => agents.filter((a) => a.status === 'pending_approval'),
    [agents]
  );

  const planById = useMemo(() => {
    const m = new Map();
    plans.forEach((p) => m.set(p.id, p));
    return m;
  }, [plans]);

  // ---------- Handlers ----------
  const toggleAgent = (id) => setExpandedAgents((prev) => ({ ...prev, [id]: !prev[id] }));

  const deleteAgent = async (agent) => {
    if (!window.confirm(`Disattivare l'agente "${agent.firstName} ${agent.lastName}"?`)) return;
    const res = await fetch(`${API}/api/admin/agents/${agent.id}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    if (res.status === 401) return handleUnauthorized();
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      toast({ title: 'Agente disattivato' });
      load();
    } else {
      toast({ title: 'Errore', description: data.detail || `HTTP ${res.status}`, variant: 'destructive' });
    }
  };

  const deletePlan = async (plan) => {
    if (!window.confirm(`Eliminare il piano "${plan.name}"?`)) return;
    const res = await fetch(`${API}/api/admin/agents/commission-plans/${plan.id}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    if (res.status === 401) return handleUnauthorized();
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      toast({ title: 'Piano eliminato' });
      load();
    } else {
      toast({ title: 'Errore', description: data.detail || `HTTP ${res.status}`, variant: 'destructive' });
    }
  };

  const toggleLeads2 = async (agent) => {
    const next = !agent.canManageLeads2;
    const res = await fetch(`${API}/api/admin/agents/${agent.id}`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ canManageLeads2: next }),
    });
    if (res.status === 401) return handleUnauthorized();
    if (res.ok) {
      toast({ title: next ? 'Accesso LEAD 2.0 attivato' : 'Accesso LEAD 2.0 disattivato', description: `${agent.firstName} ${agent.lastName}` });
      load();
    } else {
      toast({ title: 'Errore', variant: 'destructive' });
    }
  };

  const resetPassword = async (agent) => {
    const newPwd = window.prompt(`Nuova password per ${agent.firstName} ${agent.lastName} (min 8 caratteri):`);
    if (!newPwd || newPwd.length < 8) return;
    const res = await fetch(`${API}/api/admin/agents/${agent.id}/reset-password`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ new_password: newPwd }),
    });
    if (res.status === 401) return handleUnauthorized();
    if (res.ok) {
      toast({ title: 'Password reset', description: 'Ricordati di comunicarla all\'agente' });
    } else {
      toast({ title: 'Errore reset password', variant: 'destructive' });
    }
  };

  // ---------- Render ----------
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Agenti & Provvigioni</h1>
            <p className="mt-1 text-gray-600">Gestisci la rete vendita e i piani provvigionali del portale.</p>
          </div>
          {activeTab === 'agents' ? (
            <button type="button"
              onClick={() => setCreatingAgent({ parentAgentId: null })}
              data-testid="new-agent-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            >
              <Plus className="w-4 h-4" /> Nuovo Agente
            </button>
          ) : (
            <button type="button"
              onClick={() => setCreatingPlan(true)}
              data-testid="new-plan-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            >
              <Plus className="w-4 h-4" /> Nuovo Piano
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 flex gap-6">
          <TabButton active={activeTab === 'agents'} onClick={() => navigate('/admin/agents')} testid="tab-agents">
            <Users className="w-4 h-4 inline mr-1" /> Elenco Agenti ({agents.filter((a) => a.userType === 'agent').length})
          </TabButton>
          <TabButton active={activeTab === 'plans'} onClick={() => navigate('/admin/agents/commission-plans')} testid="tab-plans">
            <Percent className="w-4 h-4 inline mr-1" /> Piani Provvigionali ({plans.length})
          </TabButton>
        </div>

        {loading ? (
          <div className="py-16 text-center"><LoadingSpinner /></div>
        ) : activeTab === 'agents' ? (
          <>
            {/* Candidature pubbliche in attesa */}
            {pendingApplicants.length > 0 && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 mb-4" data-testid="pending-applicants-panel">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-amber-900 flex items-center gap-2">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-600" />
                      </span>
                      Candidature in attesa di approvazione ({pendingApplicants.length})
                    </h3>
                    <p className="text-xs text-amber-800 mt-0.5">Candidati che si sono registrati dal form pubblico <span className="font-mono">/diventa-agente</span>. Attivali assegnando piano provvigioni.</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {pendingApplicants.map((a) => (
                    <div key={a.id} className="bg-white rounded-lg p-3 flex flex-wrap items-center gap-3 border border-amber-100" data-testid={`pending-row-${a.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900">{a.firstName} {a.lastName}</div>
                        <div className="text-xs text-gray-600">{a.email} · {a.phone || 'senza telefono'}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
                          <span>Ricevuto {a.createdAt ? new Date(a.createdAt).toLocaleDateString('it-IT') : '—'}</span>
                          {a.codiceFiscale && <span>· CF <span className="font-mono">{a.codiceFiscale}</span></span>}
                          {a.partitaIVA && <span>· P.IVA <span className="font-mono">{a.partitaIVA}</span></span>}
                          {a.enasarcoIscritto ? (
                            <span className="text-green-700">· ✓ ENASARCO {a.enasarcoNumero || 'n° non fornito'}</span>
                          ) : (
                            <span className="text-gray-400">· ENASARCO non iscritto</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => setApproving(a)}
                        data-testid={`btn-approve-${a.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-semibold shadow hover:shadow-md"
                      >
                        Attiva e assegna piano →
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <AgentsTable
              hierarchy={hierarchy}
              planById={planById}
              expanded={expandedAgents}
              onToggle={toggleAgent}
              onEdit={setEditingAgent}
              onDelete={deleteAgent}
              onAddSub={(a) => setCreatingAgent({ parentAgentId: a.id, parentLabel: `${a.firstName} ${a.lastName}` })}
              onResetPassword={resetPassword}
              onToggleLeads2={toggleLeads2}
            />
          </>
        ) : (
          <PlansTable
            plans={plans}
            onEdit={setEditingPlan}
            onDelete={deletePlan}
          />
        )}
      </div>

      {(editingAgent || creatingAgent) && (
        <AgentModal
          initial={editingAgent}
          prefill={creatingAgent}
          plans={plans}
          agents={agents}
          onClose={() => { setEditingAgent(null); setCreatingAgent(null); }}
          onSaved={(created, initPwd) => {
            setEditingAgent(null);
            setCreatingAgent(null);
            if (created && initPwd) setNewlyCreated({ agent: created, initialPassword: initPwd });
            load();
          }}
          authHeaders={authHeaders}
          toast={toast}
          onUnauthorized={handleUnauthorized}
        />
      )}

      {(editingPlan || creatingPlan) && (
        <PlanModal
          initial={editingPlan}
          onClose={() => { setEditingPlan(null); setCreatingPlan(false); }}
          onSaved={() => { setEditingPlan(null); setCreatingPlan(false); load(); }}
          authHeaders={authHeaders}
          toast={toast}
          onUnauthorized={handleUnauthorized}
        />
      )}

      {newlyCreated && (
        <NewAgentCredentialsModal
          agent={newlyCreated.agent}
          password={newlyCreated.initialPassword}
          onClose={() => setNewlyCreated(null)}
        />
      )}

      {approving && (
        <ApproveAgentModal
          applicant={approving}
          plans={plans}
          onClose={() => setApproving(null)}
          onApproved={(agent, initPwd, code) => {
            setApproving(null);
            setNewlyCreated({ agent: { ...agent, code }, initialPassword: initPwd });
            load();
          }}
          authHeaders={authHeaders}
          toast={toast}
        />
      )}
    </AdminLayout>
  );
};

// ============================================================
// AGENTS TABLE
// ============================================================
const AgentsTable = ({ hierarchy, planById, expanded, onToggle, onEdit, onDelete, onAddSub, onResetPassword, onToggleLeads2 }) => {
  if (hierarchy.length === 0) {
    return <EmptyState label="Nessun agente. Crea il primo agente per iniziare." />;
  }
  return (
    <div className="bg-white rounded-lg shadow-md divide-y divide-gray-100">
      <div className="grid grid-cols-[40px_1fr_140px_140px_120px_120px_210px] items-center px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 rounded-t-lg">
        <span></span>
        <span>Agente</span>
        <span>Codice</span>
        <span>Piano</span>
        <span>Clienti</span>
        <span>Stato</span>
        <span className="text-right pr-2">Azioni</span>
      </div>
      {hierarchy.map((a) => (
        <React.Fragment key={a.id}>
          <AgentRow
            agent={a}
            plan={a.commissionPlanId ? planById.get(a.commissionPlanId) : null}
            expanded={expanded[a.id]}
            hasSubAgents={a.subAgents.length > 0}
            isSubAgent={false}
            onToggle={() => onToggle(a.id)}
            onEdit={() => onEdit(a)}
            onDelete={() => onDelete(a)}
            onAddSub={() => onAddSub(a)}
            onResetPassword={() => onResetPassword(a)}
            onToggleLeads2={() => onToggleLeads2(a)}
          />
          {expanded[a.id] && a.subAgents.map((sa) => (
            <AgentRow
              key={sa.id}
              agent={sa}
              plan={sa.commissionPlanId ? planById.get(sa.commissionPlanId) : null}
              isSubAgent
              onEdit={() => onEdit(sa)}
              onDelete={() => onDelete(sa)}
              onResetPassword={() => onResetPassword(sa)}
              onToggleLeads2={() => onToggleLeads2(sa)}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};

const AgentRow = ({ agent, plan, expanded, hasSubAgents, isSubAgent, onToggle, onEdit, onDelete, onAddSub, onResetPassword, onToggleLeads2 }) => {
  const effectiveRate = agent.commissionRateOverride ?? plan?.default_rate ?? '—';
  return (
    <div
      className={`grid grid-cols-[40px_1fr_140px_140px_120px_120px_210px] items-center px-4 py-3 ${isSubAgent ? 'bg-purple-50/30 pl-10' : ''} hover:bg-purple-50/60`}
      data-testid={`agent-row-${agent.id}`}
    >
      <button
        type="button"
        onClick={hasSubAgents ? onToggle : undefined}
        disabled={!hasSubAgents && !isSubAgent}
        className="flex items-center justify-center text-gray-500 hover:text-purple-600 disabled:opacity-30"
      >
        {isSubAgent ? <span className="text-gray-400">↳</span>
          : hasSubAgents ? (expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)
          : <Users className="w-4 h-4 opacity-30" />}
      </button>
      <div className="min-w-0">
        <div className="font-semibold text-gray-900 truncate">
          {agent.firstName} {agent.lastName}
          {isSubAgent && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">SUB</span>}
        </div>
        <div className="text-[11px] text-gray-500 truncate">{agent.email}</div>
      </div>
      <span className="font-mono text-xs text-gray-700">{agent.code}</span>
      <div className="min-w-0">
        {plan ? (
          <>
            <div className="text-xs text-gray-700 truncate">{plan.name}</div>
            <div className="text-[10px] font-mono text-purple-700">{effectiveRate}%</div>
          </>
        ) : (
          <span className="text-[11px] italic text-gray-400">Nessun piano</span>
        )}
      </div>
      <div className="text-sm">
        <span className="font-mono">{agent.customerCount || 0}</span>
        {(agent.subAgentCount || 0) > 0 && (
          <span className="text-[10px] text-gray-500 ml-1">+{agent.subAgentCount} sub</span>
        )}
      </div>
      <div>
        {agent.isActive ? (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">Attivo</span>
        ) : (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-600">Disattivato</span>
        )}
      </div>
      <div className="flex items-center justify-end gap-1.5 pr-1">
        <IconBtn onClick={onToggleLeads2} title={agent.canManageLeads2 ? 'Accesso LEAD 2.0 attivo — clicca per revocare' : 'Abilita accesso LEAD 2.0'} testid={`agent-toggle-leads2-${agent.id}`}>
          <LayoutGrid className={`w-4 h-4 ${agent.canManageLeads2 ? 'text-fuchsia-600' : 'text-gray-300'}`} />
        </IconBtn>
        {!isSubAgent && (
          <IconBtn onClick={onAddSub} title="Aggiungi sub-agente" testid={`agent-add-sub-${agent.id}`}>
            <Plus className="w-4 h-4 text-purple-600" />
          </IconBtn>
        )}
        <IconBtn onClick={onResetPassword} title="Reset password" testid={`agent-reset-pwd-${agent.id}`}>
          <KeyRound className="w-4 h-4 text-amber-600" />
        </IconBtn>
        <IconBtn onClick={onEdit} title="Modifica" testid={`agent-edit-${agent.id}`}>
          <Edit className="w-4 h-4 text-blue-600" />
        </IconBtn>
        <IconBtn onClick={onDelete} title="Disattiva" testid={`agent-delete-${agent.id}`}>
          <Trash2 className="w-4 h-4 text-red-600" />
        </IconBtn>
      </div>
    </div>
  );
};

// ============================================================
// PLANS TABLE
// ============================================================
const PlansTable = ({ plans, onEdit, onDelete }) => {
  if (plans.length === 0) {
    return <EmptyState label="Nessun piano provvigionale. Crea il primo per iniziare." />;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {plans.map((p) => (
        <div key={p.id} className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow" data-testid={`plan-row-${p.id}`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 truncate">{p.name}</h3>
              <p className="text-xs text-gray-500 truncate">{p.description || '—'}</p>
            </div>
            <span className="text-3xl font-bold text-purple-600 font-mono">{p.default_rate}%</span>
          </div>
          <div className="text-xs text-gray-600 mb-3">
            <span className="font-semibold">Trigger pagamento:</span>{' '}
            {p.payout_trigger === 'quote_accepted' ? 'Accettazione preventivo'
              : p.payout_trigger === 'order_confirmed' ? 'Conferma ordine'
              : 'Pagamento saldo (consigliato)'}
          </div>
          {p.rules && p.rules.length > 0 && (
            <div className="text-xs text-gray-700 mb-3 bg-gray-50 rounded p-2">
              <div className="font-semibold text-gray-500 uppercase text-[10px] mb-1">Regole speciali</div>
              {p.rules.map((r, i) => (
                <div key={i} className="text-[11px] font-mono">
                  • {r.by === 'quantity_range' ? `Q ${r.min || 0}-${r.max || '∞'}`
                    : r.by === 'product_category' ? `Categoria "${r.key}"`
                    : r.by === 'user_type' ? `Tipo cliente "${r.key}"`
                    : `Prodotto "${r.key}"`} → {r.rate}%
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => onEdit(p)} data-testid={`plan-edit-${p.id}`}
              className="p-1.5 rounded hover:bg-gray-100"><Edit className="w-4 h-4 text-blue-600" /></button>
            <button onClick={() => onDelete(p)} data-testid={`plan-delete-${p.id}`}
              className="p-1.5 rounded hover:bg-gray-100"><Trash2 className="w-4 h-4 text-red-600" /></button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// AGENT MODAL — Create/Edit
// ============================================================
const AgentModal = ({ initial, prefill, plans, agents, onClose, onSaved, authHeaders, toast, onUnauthorized }) => {
  const isEdit = !!initial;
  const parentAgentId = prefill?.parentAgentId;
  const isSubAgent = isEdit ? initial.userType === 'sub-agent' : !!parentAgentId;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    email: initial?.email || '',
    password: '',
    firstName: initial?.firstName || '',
    lastName: initial?.lastName || '',
    phone: initial?.phone || '',
    agentType: isSubAgent ? 'sub-agent' : 'agent',
    parentAgentId: initial?.parentAgentId || parentAgentId || '',
    code: initial?.code || '',
    commissionPlanId: initial?.commissionPlanId || '',
    commissionRateOverride: initial?.commissionRateOverride ?? '',
    iban: initial?.iban || '',
    contractUrl: initial?.contractUrl || '',
    notes: initial?.notes || '',
    isActive: initial?.isActive ?? true,
  }));

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = isEdit
        ? `${API}/api/admin/agents/${initial.id}`
        : `${API}/api/admin/agents`;
      const method = isEdit ? 'PUT' : 'POST';

      // Costruisci payload
      const payload = isEdit ? {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone || null,
        code: form.code || null,
        commissionPlanId: form.commissionPlanId || null,
        commissionRateOverride: form.commissionRateOverride === '' ? null : parseFloat(form.commissionRateOverride),
        iban: form.iban || null,
        contractUrl: form.contractUrl || null,
        notes: form.notes || null,
        isActive: form.isActive,
      } : {
        email: form.email.trim().toLowerCase(),
        password: form.password || null,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone || null,
        agentType: form.agentType,
        parentAgentId: form.parentAgentId || null,
        code: form.code || null,
        commissionPlanId: form.commissionPlanId || null,
        commissionRateOverride: form.commissionRateOverride === '' ? null : parseFloat(form.commissionRateOverride),
        iban: form.iban || null,
        contractUrl: form.contractUrl || null,
        notes: form.notes || null,
      };

      // Basic validation
      if (!isEdit && !form.email) {
        toast({ title: 'Email obbligatoria', variant: 'destructive' });
        setSaving(false);
        return;
      }
      if (!form.firstName || !form.lastName) {
        toast({ title: 'Nome e cognome obbligatori', variant: 'destructive' });
        setSaving(false);
        return;
      }
      if (form.agentType === 'sub-agent' && !form.parentAgentId) {
        toast({ title: 'Seleziona un agente parent per il sub-agente', variant: 'destructive' });
        setSaving(false);
        return;
      }

      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      if (res.status === 401 && onUnauthorized) { onUnauthorized(); return; }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Backend può rispondere in due formati: {detail: ...} (FastAPI) o {message, errors:[...]} (custom)
        let msg;
        if (typeof data.detail === 'string') msg = data.detail;
        else if (Array.isArray(data.detail) && data.detail.length > 0) msg = data.detail.map((e) => e.msg || JSON.stringify(e)).join('; ');
        else if (typeof data.message === 'string') msg = data.message;
        else if (Array.isArray(data.errors) && data.errors.length > 0) msg = data.errors.map((e) => (typeof e === 'string' ? e : (e.msg || e.message || JSON.stringify(e)))).join('; ');
        else msg = `HTTP ${res.status}`;
        throw new Error(msg);
      }
      toast({ title: isEdit ? 'Agente aggiornato' : 'Agente creato' });
      onSaved(data.data, data.data?.initialPassword);
    } catch (e) {
      toast({ title: 'Errore', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const topLevelAgents = agents.filter((a) => a.userType === 'agent' && a.isActive);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="agent-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-gray-900">
            {isEdit ? 'Modifica' : 'Nuovo'} {isSubAgent ? 'sub-agente' : 'agente'}
            {prefill?.parentLabel && <span className="ml-2 text-sm text-gray-500">sotto: {prefill.parentLabel}</span>}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipo agente *">
                <select value={form.agentType}
                  onChange={(e) => setField('agentType', e.target.value)}
                  disabled={!!parentAgentId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="agent">Agente (livello 1)</option>
                  <option value="sub-agent">Sub-agente</option>
                </select>
              </Field>
              {form.agentType === 'sub-agent' && (
                <Field label="Agente parent *">
                  <select value={form.parentAgentId}
                    onChange={(e) => setField('parentAgentId', e.target.value)}
                    disabled={!!parentAgentId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">— Seleziona —</option>
                    {topLevelAgents.map((a) => (
                      <option key={a.id} value={a.id}>{a.firstName} {a.lastName} ({a.code})</option>
                    ))}
                  </select>
                </Field>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome *">
              <input value={form.firstName} onChange={(e) => setField('firstName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="agent-input-first" />
            </Field>
            <Field label="Cognome *">
              <input value={form.lastName} onChange={(e) => setField('lastName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="agent-input-last" />
            </Field>
          </div>

          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email *" hint="Verrà usata per il login">
                <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="agent-input-email" />
              </Field>
              <Field label="Password" hint="Lascia vuoto per generarla automaticamente (min 8 caratteri)">
                <input type="text" value={form.password} onChange={(e) => setField('password', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono" placeholder="auto" />
              </Field>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefono">
              <input value={form.phone} onChange={(e) => setField('phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="+39..." />
            </Field>
            <Field label="Codice commerciale" hint="Es. AG-001. Lascia vuoto per auto-genera.">
              <input value={form.code} onChange={(e) => setField('code', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono" placeholder="auto" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Piano provvigionale">
              <select value={form.commissionPlanId}
                onChange={(e) => setField('commissionPlanId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="agent-input-plan"
              >
                <option value="">— Nessun piano —</option>
                {plans.filter((p) => p.is_active).map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.default_rate}%)</option>
                ))}
              </select>
            </Field>
            <Field label="% Override" hint="Sovrascrive la % del piano solo per questo agente. Lascia vuoto per usare piano.">
              <div className="relative">
                <input type="number" step="0.1" min="0" max="100"
                  value={form.commissionRateOverride}
                  onChange={(e) => setField('commissionRateOverride', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pr-8" placeholder="auto" />
                <span className="absolute right-3 top-2 text-gray-400">%</span>
              </div>
            </Field>
          </div>

          <Field label="IBAN">
            <input value={form.iban} onChange={(e) => setField('iban', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono" placeholder="IT60X..." />
          </Field>

          <Field label="URL contratto (opzionale)">
            <input value={form.contractUrl} onChange={(e) => setField('contractUrl', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="https://..." />
          </Field>

          <Field label="Note interne">
            <textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)}
              rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
          </Field>

          {isEdit && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.isActive}
                onChange={(e) => setField('isActive', e.target.checked)}
                className="rounded text-purple-600" />
              Agente attivo (visibile in portale)
            </label>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700">Annulla</button>
          <button onClick={handleSave} disabled={saving}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 disabled:opacity-50"
            data-testid="agent-modal-save"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Salvataggio…' : (isEdit ? 'Salva' : 'Crea')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// PLAN MODAL
// ============================================================
const PlanModal = ({ initial, onClose, onSaved, authHeaders, toast, onUnauthorized }) => {
  const isEdit = !!initial;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    name: initial?.name || '',
    description: initial?.description || '',
    default_rate: initial?.default_rate ?? 8,
    rules: initial?.rules || [],
    payout_trigger: initial?.payout_trigger || 'order_paid',
    is_active: initial?.is_active ?? true,
  }));

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const addRule = () => setField('rules', [...form.rules, { by: 'product_category', key: '', rate: 10 }]);
  const updateRule = (i, patch) => setField('rules', form.rules.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRule = (i) => setField('rules', form.rules.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = isEdit
        ? `${API}/api/admin/agents/commission-plans/${initial.id}`
        : `${API}/api/admin/agents/commission-plans`;
      const method = isEdit ? 'PUT' : 'POST';
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        default_rate: parseFloat(form.default_rate),
        rules: form.rules.map((r) => ({
          by: r.by,
          key: r.key || null,
          min: r.min === '' ? null : (r.min != null ? parseInt(r.min, 10) : null),
          max: r.max === '' ? null : (r.max != null ? parseInt(r.max, 10) : null),
          rate: parseFloat(r.rate),
        })),
        payout_trigger: form.payout_trigger,
        is_active: form.is_active,
      };
      if (!payload.name) { toast({ title: 'Nome obbligatorio', variant: 'destructive' }); setSaving(false); return; }
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        let msg;
        if (typeof data.detail === 'string') msg = data.detail;
        else if (Array.isArray(data.detail) && data.detail.length > 0) msg = data.detail.map((e) => e.msg || JSON.stringify(e)).join('; ');
        else if (typeof data.message === 'string') msg = data.message;
        else if (Array.isArray(data.errors) && data.errors.length > 0) msg = data.errors.map((e) => (typeof e === 'string' ? e : (e.msg || e.message || JSON.stringify(e)))).join('; ');
        else msg = `HTTP ${res.status}`;
        throw new Error(msg);
      }
      toast({ title: isEdit ? 'Piano aggiornato' : 'Piano creato' });
      onSaved();
    } catch (e) {
      toast({ title: 'Errore', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="plan-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-gray-900">{isEdit ? 'Modifica' : 'Nuovo'} Piano Provvigionale</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Nome piano *">
            <input value={form.name} onChange={(e) => setField('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="plan-input-name" />
          </Field>
          <Field label="Descrizione">
            <textarea value={form.description} onChange={(e) => setField('description', e.target.value)}
              rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="% Default *" hint="Provvigione base per ordini standard">
              <div className="relative">
                <input type="number" step="0.1" min="0" max="100"
                  value={form.default_rate} onChange={(e) => setField('default_rate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pr-8"
                  data-testid="plan-input-rate" />
                <span className="absolute right-3 top-2 text-gray-400">%</span>
              </div>
            </Field>
            <Field label="Quando matura la provvigione *">
              <select value={form.payout_trigger} onChange={(e) => setField('payout_trigger', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                <option value="quote_accepted">Accettazione preventivo</option>
                <option value="order_confirmed">Conferma ordine</option>
                <option value="order_paid">Pagamento saldo (consigliato)</option>
              </select>
            </Field>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-700">Regole speciali (override)</label>
              <button onClick={addRule} type="button"
                className="text-xs font-semibold text-purple-600 hover:text-purple-800 inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Aggiungi regola
              </button>
            </div>
            {form.rules.length === 0 && (
              <p className="text-[11px] italic text-gray-400">Nessuna regola. Usa il default {form.default_rate}%.</p>
            )}
            <div className="space-y-2">
              {form.rules.map((r, i) => (
                <div key={i} className="grid grid-cols-[140px_1fr_80px_80px_80px_32px] gap-2 items-center text-xs">
                  <select value={r.by} onChange={(e) => updateRule(i, { by: e.target.value })}
                    className="px-2 py-1.5 border border-gray-300 rounded">
                    <option value="product_category">Categoria prod.</option>
                    <option value="quantity_range">Fascia quantità</option>
                    <option value="user_type">Tipo cliente</option>
                    <option value="product_id">Prodotto specifico</option>
                  </select>
                  <input value={r.key || ''} onChange={(e) => updateRule(i, { key: e.target.value })}
                    disabled={r.by === 'quantity_range'}
                    placeholder={r.by === 'product_category' ? 'gin, amaro, ...'
                      : r.by === 'user_type' ? 'vip, prospect, ...'
                      : r.by === 'product_id' ? 'amaro-agrumato' : '—'}
                    className="px-2 py-1.5 border border-gray-300 rounded" />
                  <input type="number" value={r.min ?? ''} onChange={(e) => updateRule(i, { min: e.target.value })}
                    disabled={r.by !== 'quantity_range'}
                    placeholder="min" className="px-2 py-1.5 border border-gray-300 rounded font-mono disabled:bg-gray-100" />
                  <input type="number" value={r.max ?? ''} onChange={(e) => updateRule(i, { max: e.target.value })}
                    disabled={r.by !== 'quantity_range'}
                    placeholder="max" className="px-2 py-1.5 border border-gray-300 rounded font-mono disabled:bg-gray-100" />
                  <div className="relative">
                    <input type="number" step="0.1" value={r.rate} onChange={(e) => updateRule(i, { rate: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded font-mono pr-5" />
                    <span className="absolute right-2 top-1.5 text-gray-400">%</span>
                  </div>
                  <button onClick={() => removeRule(i)} type="button"
                    className="p-1.5 rounded hover:bg-red-50 text-red-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_active}
              onChange={(e) => setField('is_active', e.target.checked)}
              className="rounded text-purple-600" />
            Piano attivo (disponibile per gli agenti)
          </label>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700">Annulla</button>
          <button onClick={handleSave} disabled={saving} data-testid="plan-modal-save"
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 disabled:opacity-50">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Salvataggio…' : (isEdit ? 'Salva' : 'Crea')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// NEW AGENT CREDENTIALS MODAL — mostra password iniziale
// ============================================================
const NewAgentCredentialsModal = ({ agent, password, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copyAll = () => {
    const text = `Portale IlTuoAmaro — credenziali agente\n\nEmail: ${agent.email}\nPassword iniziale: ${password}\n\nAccesso: https://iltuoamaro.it/login\n\nRicorda di cambiare la password al primo login.`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-bold text-gray-900">Agente creato</h3>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700">Comunica queste credenziali all&apos;agente. La password non sarà più visibile dopo aver chiuso.</p>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2">
            <div><span className="text-xs font-semibold text-gray-500">EMAIL</span><div className="font-mono text-sm">{agent.email}</div></div>
            <div><span className="text-xs font-semibold text-gray-500">PASSWORD INIZIALE</span><div className="font-mono text-sm select-all">{password}</div></div>
            <div><span className="text-xs font-semibold text-gray-500">CODICE</span><div className="font-mono text-sm">{agent.code}</div></div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <button onClick={copyAll}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md bg-purple-600 text-white hover:bg-purple-700"
              data-testid="copy-credentials-btn"
            >
              <Copy className="w-4 h-4" /> {copied ? 'Copiato!' : 'Copia tutto'}
            </button>
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900">Chiudi</button>
          </div>
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>Ricorda di comunicare le credenziali in modo sicuro. L&apos;agente dovrebbe cambiare la password al primo login.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// HELPERS UI
// ============================================================
const TabButton = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-1 pb-3 -mb-px text-sm font-semibold border-b-2 transition-colors ${
      active ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >{children}</button>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

const IconBtn = ({ onClick, children, title, testid }) => (
  <button type="button" onClick={onClick} title={title} data-testid={testid}
    className="p-1.5 rounded hover:bg-gray-100 transition-colors">{children}</button>
);

const EmptyState = ({ label }) => (
  <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">{label}</div>
);

// ============================================================
// APPROVE AGENT MODAL — attiva candidatura da form pubblico
// ============================================================
const ApproveAgentModal = ({ applicant, plans, onClose, onApproved, authHeaders, toast }) => {
  const [planId, setPlanId] = useState((plans[0] && plans[0].id) || '');
  const [override, setOverride] = useState('');
  const [code, setCode] = useState('');
  const [notes, setNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);
  const activePlans = plans.filter((p) => p.is_active);

  const submit = async () => {
    if (!planId) { toast({ title: 'Seleziona un piano provvigionale', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/agents/${applicant.id}/approve`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          commissionPlanId: planId,
          commissionRateOverride: override ? parseFloat(override) : null,
          code: code || null,
          notes: notes || null,
          sendWelcomeEmail: sendEmail,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Errore approvazione');
      toast({ title: 'Agente attivato!', description: `Codice: ${j.code} · email di benvenuto ${sendEmail ? 'inviata' : 'NON inviata'}` });
      onApproved(j.data, j.initialPassword, j.code);
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="approve-agent-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4 rounded-t-2xl">
          <h2 className="text-lg font-semibold">Attiva agente</h2>
          <div className="text-sm opacity-90">{applicant.firstName} {applicant.lastName} · {applicant.email}</div>
        </div>
        <div className="p-6 space-y-4 text-sm">
          {/* Dati candidatura sintetici */}
          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-700 space-y-0.5">
            <div><b>Telefono:</b> {applicant.phone || '—'}</div>
            <div><b>Ricevuta:</b> {applicant.createdAt ? new Date(applicant.createdAt).toLocaleString('it-IT') : '—'}</div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">Piano provvigionale *</label>
            <select value={planId} onChange={(e) => setPlanId(e.target.value)}
              data-testid="approve-plan-select"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
              <option value="">— seleziona —</option>
              {activePlans.map((p) => (
                <option key={p.id} value={p.id}>{p.name} — {p.default_rate}%</option>
              ))}
            </select>
            {activePlans.length === 0 && (
              <div className="mt-1 text-xs text-red-600">Nessun piano attivo. Creane uno dal tab &quot;Piani Provvigionali&quot;.</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase">Override %</label>
              <input type="number" min="0" max="100" step="0.5" value={override} onChange={(e) => setOverride(e.target.value)}
                placeholder="lasciare vuoto per usare piano"
                data-testid="approve-override"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase">Codice agente</label>
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="auto-generato se vuoto"
                data-testid="approve-code"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">Note interne</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
              data-testid="approve-send-email"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500" />
            <span>Invia email di benvenuto con credenziali di accesso al portale</span>
          </label>
        </div>
        <div className="border-t px-6 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md text-sm">Annulla</button>
          <button onClick={submit} disabled={saving || !planId}
            data-testid="btn-approve-submit"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-md text-sm font-semibold disabled:opacity-50">
            {saving ? 'Attivazione…' : 'Attiva agente'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAgents;
